//#region IMPORTS
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Guid } from 'guid-typescript';
import { IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  PermMedia,
  SyncAlt as SyncAltIcon,
  Undo,
  Warning
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';

// COMPONENTS / STYLES
import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';
import TypographyPriceCap from '@/main/components/common/typography/typographyPriceCap';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import QuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import ModalImagesReturn from '@/main/screens/order/viewOrder/components/modalImagesReturn/modalImagesReturn';

// SERVICES / UTILS
import { procurementApi } from '@/main/api';
import { editSquareSVG } from '@/main/components/shared/icons/svg/editSquareSVG';
import { GetImagePartRequest } from '@/main/models/request';
import {
  GetImagePartResponse,
  ManageStatusItemsResponse,
  ProcurementHeaderViewOrderResponse,
  ProcurementSupplierItem
} from '@/main/models/response';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { IHandleSetInfo } from '@/main/screens/order/viewOrder/components/cardGrid/cardGrid';
import { InitialStateEditPart } from '@/main/screens/order/viewOrder/components/infoOrder/infoOrder';
import { isReturnablePart } from '@/main/screens/order/viewOrder/components/infoOrder/utilsInfoOrder';
import { Controls } from '@/main/services/constants/constantsControl';
import { FileTypeEnum } from '@/main/services/enum/FileTypeEnum';
import { OrderStatusEnum } from '@/main/services/enum/OrderStatusEnum';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';
import {
  buildUrlQueryParams,
  getTranslateByKey,
  isEmpty,
  isNullUndefined,
  isNullUndefinedEmpty,
  isShowControlByElementId
} from '@/main/services/utils';
import { formatCurrency } from '@/main/services/utilsNumber';
import { hasValueString } from '@/main/services/utilsString';
import {
  userIsAdmin,
  userIsBodyshop,
  userIsBuyer,
  userIsInsurer,
  userIsSupplier
} from '@/main/services/utilsUser';
import { useUserReducer } from '@/main/store/user';

// COMPONENTS /STYLES LOCAL
import AsyncModalConfirmWithLoading from '../../asyncModalConfirmWithLoading/asyncModalConfirmWithLoading';
import KOGImage from '../../kogImage/kogImage';
import PriceDiffPercent from '../../priceDiffPercent/priceDiffPercent';
import * as S from './styles';
import AreaButtonsRowPart from './areaButtons/areaButtons';
import RenderTooltipMessage from '../../renderTooltipMessage/renderTooltipMessage';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
//#endregion
interface IRowPartProps {
  image: string;
  procurementSupplierItem: ProcurementSupplierItem;
  deliveryBySupplier: boolean;
  guidProcurementSupplier: string;
  guidQuoteProcurement: string;
  supplierItems: ProcurementSupplierItem[];
  guidQuote: string;
  guidCompany: string;
  isManagePackages?: boolean;
  showPrices: boolean;
  dataHeader: ProcurementHeaderViewOrderResponse | null;
  handleOpenConfirmProcess: (propConfirmProcess: IHandleSetInfo) => void;
  displayPartsDetails: (
    supplierInventoryGuid: string,
    guidQuoteSupplierItem: string
  ) => void;
  displayImagesReturnModal: (guidProcurementSupplierItem: string) => void;
  handleSetInfoReason: (propReason: IHandleSetInfo) => void;
  handleUpdateQuoteCountStatus: () => void;
  handleUpdateQuoteGridStatus: (
    guid: string,
    newStatus: number,
    newStatusSupplierName: string
  ) => void;
  setResultDataManageStatus: (data: ManageStatusItemsResponse | null) => void;
  loadViewOrder: (guidQuoteProcurement: string) => void;
  setEditPart: React.Dispatch<React.SetStateAction<InitialStateEditPart>>;
}

const useStylesTooltip = makeStyles({
  popperCustom: { marginLeft: 15 },
  noMaxWidth: {
    maxWidth: 'none'
  }
});

function RowPart({
  image,
  procurementSupplierItem,
  deliveryBySupplier,
  guidProcurementSupplier,
  guidQuoteProcurement,
  supplierItems,
  guidQuote,
  guidCompany,
  dataHeader,
  isManagePackages,
  showPrices,
  displayPartsDetails,
  handleOpenConfirmProcess,
  handleUpdateQuoteCountStatus,
  handleUpdateQuoteGridStatus,
  handleSetInfoReason,
  loadViewOrder,
  setResultDataManageStatus,
  setEditPart
}: IRowPartProps) {
  const {
    guidSupplierInventory,
    guidQuoteSupplierItem,
    guidSupplierItem,
    guidProcurementSupplierItem,
    guidReason,
    reason,
    reasonType,
    reasonDescription,
    guidPart,
    priceCapFormatted,
    priceDiffPercent,
    externalPartId,
    bodyshopResponseTime,
    quoteProcurementPartInfo,
    cancelationPending,
    canceledBySupplier,
    image1
  } = procurementSupplierItem;

  const total = procurementSupplierItem.price
    ? procurementSupplierItem.price
    : procurementSupplierItem.partPrice;
  const quantityPart =
    procurementSupplierItem.quantityPart || procurementSupplierItem.amount;
  const status = {
    id: procurementSupplierItem.idStatusPart,
    name: procurementSupplierItem.statusPartName
  };
  const isPaintProject =
    procurementSupplierItem.idPartType === PartTypeEnum.Paint;

  const isBuyer = userIsBuyer(guidCompany);
  const isAdmin = userIsAdmin();
  const isUserSupplier = userIsSupplier();
  const isBodyshop = userIsBodyshop();

  const classesTooltip = useStylesTooltip();
  const navigate = useNavigate();
  const { currentUser: userReducer } = useUserReducer();

  const [openModalImagesReturn, setOpenModalImagesReturn] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isOpenModalReopen, setIsOpenModalReopen] = useState(false);
  const [dataImages, setDataImages] = useState<GetImagePartResponse[] | null>(
    null
  );

  function handleRemovePart(idReasonType: ReasonTypeEnum) {
    const propsReason = {
      guidSupplierItem,
      open: true,
      idReasonType,
      guidProcurement: guidQuoteProcurement,
      guidProcurementSupplier,
      idProcurementStatusCompany: status.id,
      procurementSupplierItems: supplierItems,
      deliveryBySupplier: deliveryBySupplier,
      isBuyer: isBuyer || isAdmin
    };
    handleSetInfoReason(propsReason);
  }
  function handleReassignPart() {
    const routeReassignOrder = buildUrlQueryParams(
      RoutesConstants.orderReassignOrder,
      {
        guidQuote,
        guidPart,
        guidQuoteProcurement,
        guidProcurementSupplierItem,
        externalPartId
      }
    );
    navigate(routeReassignOrder);
  }
  function handleConfirmProcess(
    idReasonType: ReasonTypeEnum,
    statusId: number
  ) {
    const isReturnStatus = [
      ReasonTypeEnum.Part,
      ReasonTypeEnum.ReturnApprovalProcess
    ].includes(idReasonType);
    const propsReturnApprovalProcess: Partial<IHandleSetInfo> = {
      guidReason: guidReason,
      reason: reason,
      imagesReturnProcess: dataImages || [],
      isBuyer: isBuyer || isAdmin,
      reasonDescription: reasonDescription
    };
    const propsConfirmProcess: IHandleSetInfo = {
      guidSupplierItem,
      open: true,
      idReasonType,
      guidProcurement: guidQuoteProcurement,
      guidProcurementSupplier,
      guidProcurementSupplierItem,
      idProcurementStatusCompany: statusId,
      procurementSupplierItems: supplierItems,
      ...(isReturnStatus && propsReturnApprovalProcess)
    };
    handleOpenConfirmProcess(propsConfirmProcess);
  }
  const translateDelivered =
    status.id >= ProcurementStatusEnum.Delivered
      ? getTranslateByKey('information.deliveredOn')
      : null;
  const translateDeadline =
    status.id < ProcurementStatusEnum.Delivered
      ? `${getTranslateByKey('information.deadline')} {0}`
      : null;
  const messageDelivered =
    translateDelivered?.replace(
      '{0}',
      procurementSupplierItem.deliveryDateFormated
    ) ||
    translateDeadline?.replace(
      '{0}',
      procurementSupplierItem.deliveryDateFormated
    );

  const isBodyShop = userReducer?.idCompanyType === CompanyTypeEnum.BodyShop;
  const isVisibleForBodyShopRule = !(!isBuyer && isBodyShop);
  const isAwaitingReleaseSupplier = userIsSupplier() && status.id === ProcurementStatusEnum.WaitingRelease;

  function handleClickImage() {
    displayPartsDetails(guidSupplierInventory, guidQuoteSupplierItem);
  }
  function handleEditPart() {
    setEditPart({
      isOpen: true,
      guidQuoteProcurement,
      guidProcurementSupplierItem: guidSupplierItem
    });
  }
  function handleClickButtonImages() {
    setOpenModalImagesReturn(true);
  }

  async function reopenCancelReturn() {
    try {
      const responseApi = await procurementApi.reopenPart(
        guidProcurementSupplierItem
      );

      const resData = responseApi.data.result;
      if (!isNullUndefined(resData) && resData.orderNewStatusIdChanged) {
        handleUpdateQuoteCountStatus();
        handleUpdateQuoteGridStatus(
          guidQuoteProcurement || '',
          resData.orderNewStatusIdChanged,
          resData.orderNewStatusNameChanged
        );
      }
      loadViewOrder(guidQuoteProcurement);
      setResultDataManageStatus(resData);
    } catch (error) {
    } finally {
      setIsOpenModalReopen(false);
    }
  }
  async function handleClickReopenCancelReturn() {
    setIsOpenModalReopen(true);
  }

  //#region Functions modalImagesReturn
  async function loadImages() {
    setIsLoadingImages(true);
    const request: GetImagePartRequest = {
      guidProcurementSupplierItem: guidProcurementSupplierItem,
      idFileType: FileTypeEnum.ImagePart
    };

    procurementApi
      .getImagePart(request)
      .then(response => {
        setDataImages(response.data.result);
      })
      .finally(() => {
        setIsLoadingImages(false);
      });
  }

  useEffect(() => {
    const statusToLoadImages = [
      ProcurementStatusEnum.ReturnProcess,
      ProcurementStatusEnum.ReturnApprovalProcess
    ];

    if (
      hasValueString(guidProcurementSupplierItem) &&
      statusToLoadImages.includes(status.id) &&
      !isLoadingImages &&
      isNullUndefined(dataImages)
    ) {
      loadImages();
    }
  }, [status.id, guidProcurementSupplierItem]);
  //#endregion
  //#region Show/Hide Buttons
  const showButtonCancel =
    isShowControlByElementId(Controls.IdCancelPart) &&
    (status.id === OrderStatusEnum.Assigned ||
      (status.id === ProcurementStatusEnum.Transit &&
        !procurementSupplierItem.cancelationPending)) &&
    !dataHeader?.isStripePayment &&
    isVisibleForBodyShopRule &&
    !isAwaitingReleaseSupplier;

  const showButtonReturn =
    isShowControlByElementId(Controls.IdBtnReturn) &&
    isReturnablePart({
      bodyshopResponseTime,
      idCompanyStatus: status.id,
      guidCompany
    }) &&
    !dataHeader?.isStripePayment &&
    !isAwaitingReleaseSupplier;

  const showReassignButtonByStatus = [
    OrderStatusEnum.Assigned,
    OrderStatusEnum.Delivered,
    OrderStatusEnum.Received,
    OrderStatusEnum.Invoiced
  ];

  const showReassignButton =
    isVisibleForBodyShopRule &&
    isShowControlByElementId(Controls.IdReassignPart) &&
    (showReassignButtonByStatus.includes(status.id) ||
      (status.id === ProcurementStatusEnum.Transit &&
        !procurementSupplierItem.cancelationPending)) &&
    !dataHeader?.isStripePayment &&
    !isAwaitingReleaseSupplier;

  const showImagesButton =
    (isAdmin || isBodyshop || userIsInsurer()) &&
    !isLoadingImages &&
    !isNullUndefined(dataImages) &&
    dataImages.length > 0 &&
    (status.id === ProcurementStatusEnum.ReturnApprovalProcess ||
      status.id === ProcurementStatusEnum.ReturnProcess ||
      (status.id === ProcurementStatusEnum.ReturnProcess && isUserSupplier));

  const showEditButton =
    isShowControlByElementId(Controls.IdEditPrice) &&
    (userIsAdmin() ||
      userIsSupplier() ||
      userIsInsurer() ||
      (userIsBodyshop() && isBuyer)) &&
    !dataHeader?.isStripePayment &&
    (status.id === ProcurementStatusEnum.Assigned ||
      (status.id === ProcurementStatusEnum.Transit &&
        !procurementSupplierItem.cancelationPending) ||
      status.id === ProcurementStatusEnum.Delivered ||
      status.id === ProcurementStatusEnum.Received) &&
    !isAwaitingReleaseSupplier;

  const showReopenCancelReturn =
    isShowControlByElementId(Controls.IdReopenPartButton) &&
    procurementSupplierItem.visualizeRevertCancelation &&
    ((deliveryBySupplier &&
      [
        ProcurementStatusEnum.Cancelled,
        ProcurementStatusEnum.Returned
      ].includes(status.id)) ||
      (status.id === ProcurementStatusEnum.Transit &&
        procurementSupplierItem.cancelationPending &&
        !deliveryBySupplier)) &&
    !isAwaitingReleaseSupplier;
  //#endregion
  //#region Show/Hide Columns
  const hideStatusColumn =
    ((isAdmin || isUserSupplier) &&
      status.id == OrderStatusEnum.ReturnProcess) ||
    ((isAdmin || isUserSupplier) &&
      status.id == OrderStatusEnum.CancelProcess) ||
    ((isAdmin || userIsInsurer()) &&
      status.id == ProcurementStatusEnum.ReturnApprovalProcess);
  const hideReasonColumn = isNullUndefined(reason);
  const showShippingColumn = useKogControlElementId(
    Controls.IdShowShippingCompany
  );
  const statusNotShowDates = [
    ProcurementStatusEnum.Cancelled,
    ProcurementStatusEnum.Returned
  ];
  const showDateCollumn =
    !statusNotShowDates.includes(status.id) &&
    hasValueString(procurementSupplierItem.deliveryDateFormated);
  const showReturnApprovalProcessColumn =
    (isAdmin || userIsInsurer()) &&
    status.id == ProcurementStatusEnum.ReturnApprovalProcess;
  const showReturnColumn =
    (isAdmin || isUserSupplier) && status.id == OrderStatusEnum.ReturnProcess;
  //#endregion

  const isThreeColumns =
    !isNullUndefined(procurementSupplierItem.deliveryDateFormated) ||
    (!hideStatusColumn &&
      !isNullUndefined(procurementSupplierItem.deliveryDateFormated)) ||
    !hideReasonColumn ||
    showReturnApprovalProcessColumn;

  const havePartImage =
    !isNullUndefined(image1)
  const formatReason = !isNullUndefined(reasonDescription)
    ? `${reason} (${reasonDescription})`
    : reason;
  const isLaterCancelation = () =>
    cancelationPending && status.id === ProcurementStatusEnum.Transit;
  const isCancelatedBySupplier = () =>
    canceledBySupplier &&
    status.id === ProcurementStatusEnum.Cancelled &&
    !isUserSupplier;

  function renderTooltipMessageStatus() {
    if (isLaterCancelation()) {
      return getTranslateByKey('information.isOpenedCancelation');
    }

    if (isCancelatedBySupplier()) {
      return getTranslateByKey('information.isCancelatedBySupplier');
    }

    return '';
  }

  function getStatusColor() {
    if (isLaterCancelation() || isCancelatedBySupplier()) {
      return 'red';
    }

    return '';
  }

  return (
    <S.Container
      $isThreeColumns={isThreeColumns}
      $showShippingColumn={showShippingColumn}>
      {/* Notation: FIXED COLUMNS - This columns are fixed and will be always displayed  */}
      <>
        <KOGImage
          src={image}
          onClick={handleClickImage}
          style={{ cursor: `${havePartImage ? 'pointer' : 'default'}` }}
        />
        <S.ColumnQuantity>
          <S.AreaTitlePart>
            <QuoteProcurementPartInfo
              isTooltip
              data={quoteProcurementPartInfo}
              isEllipsisText={false}
              renderType="column"
              showLineSupplierInfo={{ info: '2lines' }}
              showIconNotes
            />
          </S.AreaTitlePart>
          <ColumnLabel
            label={''}
            content={<S.Quantity> {quantityPart}</S.Quantity>}
          />
        </S.ColumnQuantity>
      </>
      {/* Notation: DINAMIC COLUMNS - This columns are dinamic and will change based in show/hide Columns */}

      {!isManagePackages ? (
        <>
          {hideStatusColumn ? (
            ''
          ) : (
            <ColumnLabel
              label={''}
              content={
                <S.CollumnStatus $color={getStatusColor()}>
                  <RenderTooltipMessage
                    placement="top"
                    tooltipMessage={renderTooltipMessageStatus()}>
                    {status.name}
                    {(isLaterCancelation() || isCancelatedBySupplier()) && (
                      <Warning
                        fontSize="small"
                        style={{
                          color: '#fab01c',
                          margin: '-1px 2px 0 0'
                        }}
                      />
                    )}
                  </RenderTooltipMessage>
                </S.CollumnStatus>
              }
              propsContent={{
                style: { textAlign: 'center', whiteSpace: 'normal' }
              }}
            />
          )}

          {showReturnApprovalProcessColumn && (
            <ColumnLabel
              label={
                <S.TextCenter>
                  {getTranslateByKey('procurementstatus.ReturnApprovalProcess')}
                </S.TextCenter>
              }
              content={
                <S.ButtonConfirmProcess
                  disabled={isLoadingImages}
                  onClick={() =>
                    handleConfirmProcess(
                      ReasonTypeEnum.ReturnApprovalProcess,
                      status.id
                    )
                  }>
                  {getTranslateByKey('information.returnPartConfirmButton')}
                </S.ButtonConfirmProcess>
              }
            />
          )}
          {showReturnColumn && (
            <ColumnLabel
              label={
                <S.TextCenter>
                  {getTranslateByKey('procurementstatus.returnProcess')}
                </S.TextCenter>
              }
              content={
                <S.ButtonConfirmProcess
                  disabled={isLoadingImages}
                  onClick={() =>
                    handleConfirmProcess(ReasonTypeEnum.Part, status.id)
                  }>
                  {getTranslateByKey('information.returnPartConfirmButton')}
                </S.ButtonConfirmProcess>
              }
            />
          )}

          {hideReasonColumn ? (
            ''
          ) : (
            <ColumnLabel
              label={''}
              content={
                <S.AreaReason>
                  <p
                    title={`${reasonType} - ${reason}`}>{`${reasonType} - ${reason}`}</p>
                  <p title={reasonDescription}>{reasonDescription}</p>
                </S.AreaReason>
              }
              propsContent={{ style: { textAlign: 'center' } }}
            />
          )}

          {showDateCollumn && (
            <TooltipComponent
              title={
                <S.AreaTooltipDates>
                  <ColumnLabel
                    withColon
                    label={getTranslateByKey('information.pickupDaysDeadline')}
                    content={procurementSupplierItem.deliveryDateFormated}
                  />
                  {!isEmpty(procurementSupplierItem.sentDateFormated) && (
                    <ColumnLabel
                      withColon
                      label={getTranslateByKey(
                        'procurementReportScheduleStatus.Sent'
                      )}
                      content={procurementSupplierItem.sentDateFormated}
                    />
                  )}
                  {!isEmpty(procurementSupplierItem.deliveredDateFormated) && (
                    <ColumnLabel
                      withColon
                      label={getTranslateByKey('information.delivered')}
                      content={procurementSupplierItem.deliveredDateFormated}
                    />
                  )}
                  {!isEmpty(procurementSupplierItem.receivedDateFormated) && (
                    <ColumnLabel
                      withColon
                      label={getTranslateByKey('procurementstatus.received')}
                      content={procurementSupplierItem.receivedDateFormated}
                    />
                  )}
                </S.AreaTooltipDates>
              }>
              <S.LabelCenter>{messageDelivered}</S.LabelCenter>
            </TooltipComponent>
          )}
          {showShippingColumn && (
            <TooltipComponent
              placement="left"
              maxWidth={'100%'}
              title={
                <S.AreaTooltipShipping>
                  {procurementSupplierItem.shippingCompanyName || ''}
                  {procurementSupplierItem.shippingCompanyName &&
                    procurementSupplierItem.trackingCode
                    ? ' - '
                    : ''}
                  {procurementSupplierItem.trackingCode || ''}
                </S.AreaTooltipShipping>
              }>
              <S.CollumnShipping>
                <span>{procurementSupplierItem.shippingCompanyName}</span>
                <span>{procurementSupplierItem.trackingCode}</span>
              </S.CollumnShipping>
            </TooltipComponent>
          )}
        </>
      ) : (
        <>
          <ColumnLabel label={<></>} content={<></>} />
        </>
      )}
      <S.AreaPrice>
        {showPrices && (
          <>
            <S.TotalPrice translate="no">
              {isNullUndefined(total) ? '' : formatCurrency(total)}
              {!isNullUndefined(priceDiffPercent) && !isPaintProject && (
                <PriceDiffPercent diffPercent={priceDiffPercent} />
              )}
            </S.TotalPrice>
            {!isPaintProject &&
              !isUserSupplier &&
              (isNullUndefinedEmpty(
                procurementSupplierItem.systemReferencePriceFormatted
              ) ? (
                <TypographyPriceCap
                  priceCap={
                    isNullUndefined(priceCapFormatted) ? '' : priceCapFormatted
                  }
                />
              ) : (
                <p style={{ margin: '0' }} translate="no">
                  {
                    `
                    ${getTranslateByKey('information.systemReferencePrice')}: ${procurementSupplierItem.systemReferencePriceFormatted}
                    `
                  }
                </p>
              ))}
          </>
        )}
      </S.AreaPrice>

      {!isManagePackages && (
        <KogModalSideComponent
          shouldRenderOpen
          open={openModalImagesReturn}
          title={getTranslateByKey('information.viewImages')}
          closeModal={() => setOpenModalImagesReturn(false)}>
          <ModalImagesReturn
            guidProcurementSupplierItem={guidProcurementSupplierItem}
            dataImages={dataImages || []}
            isLoadingImages={isLoadingImages}
          />
        </KogModalSideComponent>
      )}

      {!isManagePackages && (
        <AreaButtonsRowPart
          showButtonCancel={showButtonCancel}
          showButtonReturn={showButtonReturn}
          showEditButton={showEditButton}
          showImagesButton={showImagesButton}
          showReassignButton={showReassignButton}
          showReopenCancelReturn={showReopenCancelReturn}
          handleClickButtonImages={handleClickButtonImages}
          handleClickReopenCancelReturn={handleClickReopenCancelReturn}
          handleReassignPart={handleReassignPart}
          handleRemovePart={handleRemovePart}
          handleEditPart={handleEditPart}
        />
      )}
      <AsyncModalConfirmWithLoading
        id="id-modal-reopen-part"
        isOpen={isOpenModalReopen}
        setIsOpen={setIsOpenModalReopen}
        label={`${getTranslateByKey('information.reopenPart')}?`}
        onConfirm={reopenCancelReturn}
        onCancel={() => setIsOpenModalReopen(false)}
      />
    </S.Container>
  );
}

export default RowPart;
